import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";

const MAX_RENDERED = 100;

export function SearchableSelect({
  value, onChange, options = [], placeholder = "Select…",
  width = "w-[280px]", testid, allLabel = null, allValue = "all",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sel = options.find((o) => o.value === value);
  const display = value === allValue && allLabel ? allLabel : sel?.label ?? placeholder;

  // Manual filtering with a render cap — cmdk renders every item otherwise,
  // which lags badly on 1,000+ client lists.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => (o.label || "").toLowerCase().includes(needle));
  }, [q, options]);
  const shown = filtered.slice(0, MAX_RENDERED);
  const hidden = filtered.length - shown.length;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} data-testid={testid}
                className={`${width} h-9 justify-between bg-white border-slate-200 font-normal text-sm`}>
          <span className="truncate">{display}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={`${width} p-0`} align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type to search…" value={q} onValueChange={setQ} autoFocus
                        data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandGroup>
              {allLabel && !q && (
                <CommandItem key="__all" value="__all"
                             onSelect={() => { onChange(allValue); setOpen(false); setQ(""); }}>
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === allValue ? "opacity-100" : "opacity-0"}`} />
                  {allLabel}
                </CommandItem>
              )}
              {shown.map((o) => (
                <CommandItem key={o.value} value={o.value}
                             onSelect={() => { onChange(o.value); setOpen(false); setQ(""); }}>
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === o.value ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate flex-1">{o.label}</span>
                  {o.meta != null && <span className="text-[10px] text-slate-400 tabular-nums ml-2">{o.meta}</span>}
                </CommandItem>
              ))}
              {hidden > 0 && (
                <div className="px-3 py-2 text-[11px] text-slate-400">
                  +{hidden.toLocaleString()} more — keep typing to narrow down
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
